import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { SITE } from "@/lib/content";
import { Button } from "@/components/ui/button";

const NAV = [
  { href: "/#leistungen", label: "Leistungen" },
  { href: "/#warum", label: "Warum uns" },
  { href: "/#ablauf", label: "Ablauf" },
  { href: "/#referenzen", label: "Referenzen" },
  { href: "/#kontakt", label: "Kontakt" },
  { href: "/#faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolledRaw, setScrolledRaw] = useState(false);
  const [open, setOpen] = useState(false);
  const shellRef = useRef(null);
  const highlightRef = useRef(null);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  // On non-home pages always render in "solid" mode (white pill, dark logo)
  const scrolled = !isHome || scrolledRaw;

  useEffect(() => {
    const onScroll = () => setScrolledRaw(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mouse-follow 3D tilt + highlight
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(1200px) rotateX(${-y * 2.2}deg) rotateY(${x * 1.6}deg) translateZ(0)`;
      if (highlightRef.current) {
        highlightRef.current.style.background = `radial-gradient(600px circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(125,211,252,0.18), transparent 55%)`;
      }
    };
    const onLeave = () => {
      el.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0)`;
      if (highlightRef.current) highlightRef.current.style.background = "transparent";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
      style={{ perspective: "1400px" }}
    >
      <div
        className={`mx-auto transition-all duration-500 ease-out pointer-events-auto ${
          scrolled
            ? "max-w-6xl mt-3 px-3 md:px-4"
            : "max-w-7xl mt-0 px-0"
        }`}
      >
        <div
          ref={shellRef}
          className={`relative transition-all duration-500 ease-out will-change-transform ${
            scrolled
              ? "rounded-full bg-white/80 backdrop-blur-2xl border border-white/60 shadow-[0_10px_40px_-8px_rgba(15,23,42,0.18),0_2px_6px_-2px_rgba(15,23,42,0.1),inset_0_1px_0_rgba(255,255,255,0.9)]"
              : "rounded-none bg-transparent border-transparent shadow-none"
          }`}
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Moving highlight */}
          <div
            ref={highlightRef}
            aria-hidden
            className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${
              scrolled ? "opacity-100 rounded-full" : "opacity-0"
            }`}
          />
          {/* Top chrome highlight strip */}
          {scrolled && (
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/90 to-transparent" />
          )}

          <div className={`relative mx-auto max-w-7xl px-6 md:px-8 lg:px-10 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16 md:h-[68px]" : "h-20 md:h-24"}`}>
        <a
          href="/"
          data-testid="logo-link"
          className="flex items-center group"
          style={{ transform: "translateZ(20px)" }}
        >
          <img
            src={scrolled ? "/kola-logo.png" : "/kola-logo-white.png"}
            alt="Kola Gebäudereinigung"
            className={`w-auto select-none transition-all duration-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.2)] ${
              scrolled ? "h-11 md:h-12" : "h-12 sm:h-14 md:h-16 lg:h-[68px]"
            }`}
            draggable="false"
          />
        </a>

            <nav className="hidden md:flex items-center gap-7" style={{ transform: "translateZ(30px)" }}>
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`relative text-sm font-medium transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-sky-500 after:transition-all hover:after:w-full ${
                    scrolled
                      ? "text-slate-700 hover:text-slate-900"
                      : "text-white/95 hover:text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]"
                  }`}
                >
                  {n.label}
                </a>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3" style={{ transform: "translateZ(25px)" }}>
              <a
                href={SITE.phoneHref}
                data-testid="nav-call"
                className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                  scrolled ? "text-slate-700 hover:text-sky-700" : "text-white hover:text-sky-200 [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]"
                }`}
              >
                <Phone className="w-4 h-4" />
                {SITE.phone}
              </a>
              <Button
                asChild
                data-testid="nav-cta"
                className="rounded-full bg-gradient-to-b from-sky-500 to-sky-700 hover:from-sky-400 hover:to-sky-600 text-white px-5 shadow-[0_8px_20px_-6px_rgba(2,132,199,0.6),inset_0_1px_0_rgba(255,255,255,0.3)] border border-sky-400/30"
              >
                <a href="/#kontakt">Angebot anfragen</a>
              </Button>
            </div>

            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((s) => !s)}
              className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border transition-colors ${
                scrolled
                  ? "border-slate-200 bg-white text-slate-700 shadow-sm"
                  : "border-white/30 bg-white/10 backdrop-blur text-white"
              }`}
              aria-label="Menü"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden mt-2 mx-3 pointer-events-auto rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden"
            >
              <div className="px-6 py-4 flex flex-col gap-1">
                {NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-m-${n.label.toLowerCase()}`}
                    className="py-3 text-slate-700 font-medium border-b border-slate-100 last:border-0"
                  >
                    {n.label}
                  </a>
                ))}
                <div className="flex gap-2 pt-3">
                  <Button asChild variant="outline" className="rounded-full flex-1">
                    <a href={SITE.phoneHref} data-testid="nav-m-call">
                      <Phone className="w-4 h-4 mr-1" /> Anrufen
                    </a>
                  </Button>
                  <Button asChild className="rounded-full flex-1 bg-sky-600 hover:bg-sky-700">
                    <a href="/#kontakt" data-testid="nav-m-cta" onClick={() => setOpen(false)}>
                      Angebot
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
